import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideChevronDown,
  lucideCircleAlert,
  lucideFileText,
  lucideRefreshCw,
  lucideTimer,
  lucideVerified,
  lucideX,
} from '@ng-icons/lucide';
import { HlmBadgeImports } from '@spartan-ng/helm/badge';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmSeparatorImports } from '@spartan-ng/helm/separator';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { environment } from '../../../../environments/environment';
import { Api } from '../../../services/api';
import { SessionService } from '../../../services/session-service';

type ApplicationStatus = 'approved' | 'pending' | 'cancelled';

interface PolicyDetails {
  policy_number?: string | null;
  start_date?: string | null;
  end_date?: string | null;
}

interface ApplicationData {
  status?: string;
  message?: string;
  application_status?: string | null;
  full_name?: string | null;
  mobile_number?: string | null;
  registration_number?: string | null;
  serial_number?: string | null;
  region?: string | null;
  approved_by?: string | null;
  policy?: PolicyDetails | null;
}

interface StatusPresentation {
  title: string;
  description: string;
  icon: string;
  badge: string;
  tone: 'success' | 'warning' | 'destructive' | 'neutral';
}

@Component({
  selector: 'app-status-check-step',
  imports: [
    RouterLink,
    NgIcon,
    HlmBadgeImports,
    HlmButtonImports,
    HlmSeparatorImports,
    HlmSpinnerImports,
  ],
  templateUrl: './status-check-step.html',
  styleUrl: './status-check-step.css',
  providers: [
    provideIcons({
      lucideChevronDown,
      lucideCircleAlert,
      lucideFileText,
      lucideRefreshCw,
      lucideTimer,
      lucideVerified,
      lucideX,
    }),
  ],
})
export class StatusCheckStep implements OnInit {
  readonly sessionService = inject(SessionService);
  private readonly apiService = inject(Api);

  readonly applicationLoading = signal(false);
  readonly applicationStatus = signal<ApplicationStatus | null>(null);
  readonly applicationData = signal<ApplicationData | null>(null);
  readonly applicationError = signal<string | null>(null);
  readonly showDetails = signal(false);

  readonly statusPresentation = computed<StatusPresentation>(() => {
    if (this.applicationError()) {
      return {
        title: 'We could not retrieve your application',
        description: this.applicationError() ?? 'Check your details and try again.',
        icon: 'lucideCircleAlert',
        badge: 'Needs attention',
        tone: 'destructive',
      };
    }

    switch (this.applicationStatus()) {
      case 'approved':
        return {
          title: 'Your application is approved',
          description: 'Your membership application has been approved and your policy is ready.',
          icon: 'lucideVerified',
          badge: 'Approved',
          tone: 'success',
        };
      case 'pending':
        return {
          title: 'Your application is under review',
          description: 'No action is needed right now. Check back later for an update.',
          icon: 'lucideTimer',
          badge: 'Pending review',
          tone: 'warning',
        };
      case 'cancelled':
        return {
          title: 'Your application was cancelled',
          description: 'Contact support if you need clarification or want to discuss the next step.',
          icon: 'lucideX',
          badge: 'Cancelled',
          tone: 'destructive',
        };
      default:
        return {
          title: 'Status is not available yet',
          description: 'Refresh the status or check again later.',
          icon: 'lucideTimer',
          badge: 'Unavailable',
          tone: 'neutral',
        };
    }
  });

  readonly hasAdditionalDetails = computed(() => {
    const data = this.applicationData();
    return Boolean(data?.region || data?.approved_by || data?.policy);
  });

  ngOnInit(): void {
    void this.checkApplicationStatus();
  }

  toggleDetails(): void {
    this.showDetails.update((visible) => !visible);
  }

  checkApplicationStatus(): Promise<boolean> {
    const data = {
      serial_number: this.sessionService.formSubData()?.user?.serial_number,
      mobile_number: this.sessionService.formSubData()?.user?.mobile_number,
    };

    this.applicationLoading.set(true);
    this.applicationError.set(null);
    this.showDetails.set(false);

    return new Promise<boolean>((resolve) => {
      this.apiService.postData(data, environment.checkApplicationStatus).subscribe({
        next: (response) => {
          this.applicationLoading.set(false);
          const body = response.body as ApplicationData | null;

          if ((response.status === 200 || response.status === 201) && body?.status === 'success') {
            this.applicationData.set(body);
            this.applicationStatus.set(this.normaliseStatus(body.application_status));
            resolve(true);
            return;
          }

          this.setError(body?.message ?? 'Application not found.');
          resolve(false);
        },
        error: (error: HttpErrorResponse) => {
          const message = this.readErrorMessage(error);
          this.setError(message);
          resolve(false);
        },
      });
    });
  }

  private normaliseStatus(status: string | null | undefined): ApplicationStatus | null {
    return status === 'approved' || status === 'pending' || status === 'cancelled' ? status : null;
  }

  private setError(message: string): void {
    this.applicationLoading.set(false);
    this.applicationError.set(message);
    this.applicationData.set(null);
    this.applicationStatus.set(null);
  }

  private readErrorMessage(error: HttpErrorResponse): string {
    const payload = error.error as { message?: unknown } | null;
    return typeof payload?.message === 'string' ? payload.message : 'Something went wrong while checking your status.';
  }
}
